import React, { Dispatch, ReactNode, SetStateAction, useState } from 'react';
import { Modal, ScrollView, Switch, View } from 'react-native';
import { createStyleSheet, UnistylesRuntime, useStyles } from 'react-native-unistyles';
import MyAppText from '../../../components/MyAppText';
import Divider from '../../../components/Divider';
import FormActions from '../../../components/FormActions';
import { WindowInformation } from '../../../services/window-information';
import Toast from 'react-native-toast-message';
import { MyCheckbox } from '../../../components/MyCheckbox';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faX } from '@fortawesome/free-solid-svg-icons';
import { translate } from '../../../services/translate';
import FormInput from '../../../components/FormInput';
import TimePicker from 'react-time-picker';
import CustomButton from '../../../components/CustomButton';
import { Tabs } from '../../../components/Tabs';
import { DEFAULT_PASTEL_COLORS, DEFAULT_PASTEL_TEXT_COLORS } from '../../../styles/colorPalettes';
import Badge from '../../../components/Badge';
import moment from 'moment';
import { deacService } from '../../../services/central-api/deac';
import { CustomTooltip } from '../../../components/CustomTooltip';
import { faSquarePlus } from '@fortawesome/free-regular-svg-icons';
import FilterDatePicker from '../../../components/Filter/components/FilterDateInput';
import { CalendarValue } from '../../../components/DateTimePicker';
import MyDateRangePicker from '../../../components/MyDateRangePicker';
import { useForm } from 'react-hook-form';

type ValuePiece = Date | null;

interface Props {
    template: VacancyTemplate;
    isModalVisible: boolean;
    setModalVisible: Dispatch<SetStateAction<boolean>>;
    onConfirm: () => void;
}

interface SectionHeaderParams {
    title: string;
    endContent?: ReactNode;
    children: ReactNode;
    viewZIndex?: number;
    childrenZIndex?: number;
    required?: boolean;
}

function SectionHeader({ children, endContent, title, viewZIndex, childrenZIndex, required }: SectionHeaderParams) {
    const { styles, theme } = useStyles(styleSheet);

    return <View style={{ flexDirection: 'column', rowGap: 16, zIndex: viewZIndex }}>
        <View style={{ flexDirection: 'column', rowGap: 8, zIndex: viewZIndex ? viewZIndex : 2 }}>
            <View style={{ flexDirection: 'row', columnGap: 8, alignItems: 'center' }}>
                <View style={{ flexDirection: 'row', gap: 3, alignContent: 'center', alignItems: 'baseline' }}>
                    <MyAppText style={styles.sectionHeaderTitle}>{translate(title)}</MyAppText>
                    {required ?
                        <MyAppText style={{ color: theme.colors.errors['300'] }}>(*)</MyAppText>
                        : null}
                </View>
                {endContent ? endContent : null}
            </View>
            <Divider style={{ zIndex: -1 }} color={theme.colors.primary['200']} />
        </View>
        <View style={{ zIndex: childrenZIndex ?? 1, gap: 4 }}>
            {children}
        </View>
    </View>;
}

interface ReportModalForm {
    period: { from: ValuePiece; to: ValuePiece; };
}

export function GenerateVacanciesModal({ isModalVisible, setModalVisible, template, onConfirm }: Props) {
    const { styles, theme } = useStyles(styleSheet);
    const windowInfo = WindowInformation();

    const defaultEmptyValues: ReportModalForm = {
        period: { from: null, to: null },
    };

    const form = useForm<ReportModalForm>({
        defaultValues: defaultEmptyValues,
        mode: 'onChange'
    });

    const [fixedApplicationsDate, setFixedApplicationsDate] = useState<boolean>(true);

    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [applicationQuantityDays, setApplicationQuantityDays] = useState<number>();
    const [startApplicationsDaysOffset, setStartApplicationsDaysOffset] = useState<number>(0);
    const [openingApplicationTime, setOpeningApplicationTime] = useState<string>('');
    const [closingApplicationTime, setClosingApplicationTime] = useState<string>('');
    const [applicationsStartInDateFilter, setApplicationsStartInDateFilter] = useState<CalendarValue>(null);
    const [applicationsStartInHourFilter, setApplicationsStartInHourFilter] = useState('');
    const [estimatedStartZIndex, setEstimatedStartZIndex] = useState<number>(2);

    const [applicationsFinishInDateFilter, setApplicationsFinishInDateFilter] = useState<CalendarValue>(null);
    const [applicationsFinishInHourFilter, setApplicationsFinishInHourFilter] = useState('');
    const [estimatedFinishZIndex, setEstimatedFinishZIndex] = useState<number>(1);

    const [vacancyConfiguration, setVacancyConfiguration] = useState<{
        days: {
            monday: boolean;
            tuesday: boolean;
            wednesday: boolean;
            thursday: boolean;
            friday: boolean;
            saturday: boolean;
            sunday: boolean;
            holyday: boolean;
        };
        activityTime: string;
        vacancyDuration: number;
    }[]>([{
        days: {
            monday: false,
            tuesday: false,
            wednesday: false,
            thursday: false,
            friday: false,
            saturday: false,
            sunday: false,
            holyday: false,
        },
        activityTime: '',
        vacancyDuration: 8
    }]);

    function isFormValid() {

        if (!form.formState.isValid) {
            return false;
        }

        if (!fixedApplicationsDate && (startApplicationsDaysOffset == 0 ||
            (applicationQuantityDays != undefined && applicationQuantityDays > 0 && startApplicationsDaysOffset < applicationQuantityDays) ||
            openingApplicationTime == '' ||
            (applicationQuantityDays != undefined && applicationQuantityDays > 0 && closingApplicationTime == ''))
        ) {
            return false;
        }

        if (fixedApplicationsDate) {
            if (applicationsStartInDateFilter === null) {
                return false;
            }

            if (applicationsFinishInDateFilter) {
                const formatDateTime = (date: Date | null, time: string) =>
                    date ? moment(`${moment(date).format('DD/MM/YYYY')} ${time}`, 'DD/MM/YYYY HH:mm:ss') : undefined;

                const applicationsStart = moment(`${moment(applicationsStartInDateFilter).format('DD/MM/YYYY')} ${applicationsStartInHourFilter}`, 'DD/MM/YYYY HH:mm');
                const applicationsFinish = formatDateTime(applicationsFinishInDateFilter, applicationsFinishInHourFilter ? `${applicationsFinishInHourFilter}:59` : '23:59:59');

                if (!applicationsStart.isBefore(applicationsFinish)) {
                    return false;
                }
            }
        }

        return vacancyConfiguration.every(config => {
            if (config.activityTime == '' ||
                config.vacancyDuration == 0 ||
                !Object.values(config.days).some(d => d)
            ) {
                return false;
            }

            return true;
        });
    }

    function applicationsFinishLaterThanStart() {
        if (applicationsStartInDateFilter && applicationsFinishInDateFilter) {
            const applicationsStart = moment(`${moment(applicationsStartInDateFilter).format('DD/MM/YYYY')} ${applicationsStartInHourFilter}`, 'DD/MM/YYYY HH:mm');
            const applicationsFinish = moment(`${moment(applicationsFinishInDateFilter).format('DD/MM/YYYY')} ${applicationsFinishInHourFilter}`, 'DD/MM/YYYY HH:mm');

            return applicationsStart.isBefore(applicationsFinish);
        }

        return true;
    }

    async function formSubmit() {
        try {
            setIsLoading(true);

            const formatDateTime = (date: Date | null, time: string) =>
                date ? moment(`${moment(date).format('DD/MM/YYYY')} ${time}`, 'DD/MM/YYYY HH:mm:ss').toDate() : undefined;

            const applicationsStart = formatDateTime(applicationsStartInDateFilter, applicationsStartInHourFilter ? `${applicationsStartInHourFilter}:00` : '00:00:00');
            const applicationsFinish = formatDateTime(applicationsFinishInDateFilter, applicationsFinishInHourFilter ? `${applicationsFinishInHourFilter}:59` : '23:59:59');

            const values = form.getValues();

            await deacService.generateVacanciesFromTemplate({
                templateId: template.id,
                applicationQuantityDays: applicationQuantityDays && applicationQuantityDays > 0 ? applicationQuantityDays : undefined,
                startApplicationsDaysOffset: startApplicationsDaysOffset && startApplicationsDaysOffset > 0 ? startApplicationsDaysOffset : undefined,
                openingApplicationTime: openingApplicationTime != '' ? openingApplicationTime : undefined,
                closingApplicationTime: closingApplicationTime != '' ? closingApplicationTime : undefined,
                fixedApplicationStart: applicationsStart,
                fixedApplicationEnd: applicationsFinish,
                periodStart: moment(values.period.from).valueOf(),
                periodEnd: moment(values.period.to).valueOf(),
                vacancyConfiguration,
            });

            Toast.show({
                type: 'sentinelxSuccess',
                text1: translate('ActionSuccessfully'),
            });
            onConfirm();

            setIsLoading(false);
            closeModal();

        } catch (err) {
            console.error(err);
            Toast.show({
                type: 'sentinelxError',
                text1: translate('unexpectedError'),
            });
        }
    }

    function clearValues() {
        setApplicationQuantityDays(0);
        setStartApplicationsDaysOffset(0);
        setOpeningApplicationTime('');
        setClosingApplicationTime('');
        form.reset();
        setVacancyConfiguration([{
            days: {
                monday: false,
                tuesday: false,
                wednesday: false,
                thursday: false,
                friday: false,
                saturday: false,
                sunday: false,
                holyday: false,
            },
            activityTime: '',
            vacancyDuration: 8
        }]);
        setApplicationsStartInDateFilter(null);
        setApplicationsStartInHourFilter('');
        setApplicationsFinishInDateFilter(null);
        setApplicationsFinishInHourFilter('');
    }

    function closeModal() {
        clearValues();
        setModalVisible(false);
    }

    function addVacancyConfiguration() {
        setVacancyConfiguration([
            ...vacancyConfiguration,
            {
                days: {
                    monday: false,
                    tuesday: false,
                    wednesday: false,
                    thursday: false,
                    friday: false,
                    saturday: false,
                    sunday: false,
                    holyday: false,
                },
                activityTime: '',
                vacancyDuration: 8
            }
        ]);
    }

    function removeVacancyConfiguration(index: number) {
        setVacancyConfiguration(vacancyConfiguration.filter((config, i) => !(i === index)));
    }

    return (
        <Modal
            transparent
            visible={isModalVisible}
            animationType='fade'
            onRequestClose={() => closeModal()}
        >
            <View style={styles.container}>
                <View style={styles.formContainer(windowInfo.isMobile)}>
                    <View style={{ flexDirection: 'column', rowGap: 8 }}>
                        <MyAppText style={styles.header}>{translate('generateVacancies')}</MyAppText>
                    </View>

                    <Tabs
                        tabs={[{
                            key: 'templateInfo',
                            label: translate('templateInfo'),
                        }, {
                            key: 'configuration',
                            label: translate('configuration'),
                        }]}
                    >
                        {({ selectedTab }) => {
                            if (selectedTab == 'templateInfo') {
                                return <View style={styles.templateInfo}>
                                    <ScrollView style={{ flex: 1 }} contentContainerStyle={{ gap: 16 }}>
                                        <View style={styles.templateInfoItem}>
                                            <MyAppText style={styles.templateInfoItemTitle}>{translate('workplace')}</MyAppText>
                                            <MyAppText style={styles.templateInfoItemDescription}>{template.workplace.name}</MyAppText>
                                        </View>
                                        <View style={styles.templateInfoItem}>
                                            <MyAppText style={styles.templateInfoItemTitle}>{translate('unit')}</MyAppText>
                                            <MyAppText style={styles.templateInfoItemDescription}>{template.workplace.actingBodyUnit.name}</MyAppText>
                                        </View>
                                        <View style={styles.templateInfoItem}>
                                            <MyAppText style={styles.templateInfoItemTitle}>{translate('place')}</MyAppText>
                                            <MyAppText style={styles.templateInfoItemDescription}>{template.workplace.address}</MyAppText>
                                        </View>
                                        <View style={styles.templateInfoItem}>
                                            <MyAppText style={styles.templateInfoItemTitle}>{translate('activityDescription')}</MyAppText>
                                            <MyAppText style={styles.templateInfoItemDescription}>{template.description}</MyAppText>
                                        </View>
                                        <View style={styles.templateInfoItem}>
                                            <MyAppText style={styles.templateInfoItemTitle}>{translate('requirements')}, {translate('levelAndTags')}</MyAppText>
                                            <View style={{ flexDirection: 'column', gap: 16 }}>
                                                <View style={{ flexDirection: 'column' }}>
                                                    <MyAppText style={styles.templateInfoItemSubTitle}>{translate('requirements')}:</MyAppText>
                                                    <MyAppText style={styles.templateInfoItemDescription}>{template.skills?.map(l => l.name).join(', ')}</MyAppText>
                                                </View>
                                                <View style={{ flexDirection: 'column' }}>
                                                    <MyAppText style={styles.templateInfoItemSubTitle}>{translate('level')}:</MyAppText>
                                                    <MyAppText style={styles.templateInfoItemDescription}>{template.levels?.map(l => l.name).join(', ')}</MyAppText>
                                                </View>
                                                <View style={{ flexDirection: 'column' }}>
                                                    <MyAppText style={styles.templateInfoItemSubTitle}>{translate('tags')}:</MyAppText>
                                                    <ScrollView style={{ maxHeight: 80 }} contentContainerStyle={{ flexDirection: 'row', columnGap: 8, rowGap: 4, flexWrap: 'wrap' }}>
                                                        {template.tags?.map((item) => {
                                                            const index = DEFAULT_PASTEL_COLORS.indexOf(item.color);
                                                            return <Badge
                                                                mode='ROUNDED'
                                                                key={item.id}
                                                                backgroundColor={item.color}
                                                                textColor={DEFAULT_PASTEL_TEXT_COLORS[index]}
                                                            >
                                                                {item.name}
                                                            </Badge>;
                                                        })}
                                                    </ScrollView>
                                                </View>

                                            </View>
                                        </View>
                                    </ScrollView>
                                </View>;
                            } else if (selectedTab == 'configuration') {
                                return <ScrollView contentContainerStyle={{ flexDirection: 'column', rowGap: 24 }}>
                                    <SectionHeader
                                        viewZIndex={4}
                                        childrenZIndex={3}
                                        title='period'
                                        endContent={
                                            <CustomTooltip
                                                content={<View style={{ flexDirection: 'column', gap: 32 }}>
                                                    <MyAppText style={{ color: theme.colors.white['500'] }}>{translate('templateVacancyGenerationPeriodText1')}</MyAppText>
                                                    <MyAppText style={{ color: theme.colors.white['500'] }}>{translate('templateVacancyGenerationPeriodText2')}</MyAppText>
                                                </View>
                                                }
                                            />
                                        }
                                    >
                                        <MyDateRangePicker
                                            minDate={moment().add(1, 'day').toDate()}
                                            label={translate('period')}
                                            form={form}
                                            fieldName='period'
                                            zIndex={2}
                                            required={true}
                                        />
                                    </SectionHeader>
                                    <SectionHeader
                                        title='applications'
                                        viewZIndex={3}
                                        endContent={
                                            <View style={{ flex: 1, alignItems: 'flex-end' }}>
                                                <View style={{ flexDirection: 'row', gap: 8, }}>
                                                    <MyAppText style={styles.toggleLabelStyle}>{translate('fixedOpening')}</MyAppText>
                                                    <Switch
                                                        value={fixedApplicationsDate}
                                                        onValueChange={(val) => {
                                                            setFixedApplicationsDate(val);
                                                            if (val) {
                                                                setApplicationQuantityDays(0);
                                                                setStartApplicationsDaysOffset(0);
                                                                setOpeningApplicationTime('');
                                                                setClosingApplicationTime('');
                                                            } else {
                                                                setApplicationsStartInDateFilter(null);
                                                                setApplicationsStartInHourFilter('');
                                                                setApplicationsFinishInDateFilter(null);
                                                                setApplicationsFinishInHourFilter('');
                                                            }
                                                        }}
                                                        trackColor={{ false: theme.colors.switch.unselectedTrack, true: theme.colors.switch.unselectedTrack }}
                                                        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                                                        // @ts-ignore
                                                        activeThumbColor={theme.colors.primary['500']}
                                                        thumbColor={theme.colors.primary['500']}
                                                    />
                                                </View>
                                            </View>
                                        }
                                    >
                                        {fixedApplicationsDate
                                            ? <View style={{ flexDirection: 'column', gap: 8 }}>
                                                <View style={[{ gap: 16 }, windowInfo.isMobile ? { flexDirection: 'column' } : { flexDirection: 'row' }]}>
                                                    <View style={[{ zIndex: estimatedStartZIndex }, windowInfo.isMobile ? {} : { maxWidth: '35%', flex: 1 }]}>
                                                        <FilterDatePicker
                                                            date={applicationsStartInDateFilter}
                                                            onChangeDate={setApplicationsStartInDateFilter}
                                                            time={applicationsStartInHourFilter}
                                                            onChangeTime={setApplicationsStartInHourFilter}
                                                            label={translate('openingDateHour')}
                                                            onCalendarOpen={() => setEstimatedStartZIndex(99999)}
                                                            onCalendarClose={() => setEstimatedStartZIndex(2)}
                                                            required
                                                            disabled={!form.watch('period').from}
                                                            maxDate={form.watch('period').from ?? undefined}
                                                            labelStyle={styles.fieldsLabelStyle}
                                                        />
                                                    </View>

                                                    <View style={[{ zIndex: estimatedFinishZIndex }, windowInfo.isMobile ? {} : { maxWidth: '35%', flex: 1 }]}>
                                                        <FilterDatePicker
                                                            date={applicationsFinishInDateFilter}
                                                            onChangeDate={setApplicationsFinishInDateFilter}
                                                            time={applicationsFinishInHourFilter}
                                                            onChangeTime={setApplicationsFinishInHourFilter}
                                                            label={translate('finalDateHour')}
                                                            onCalendarOpen={() => setEstimatedFinishZIndex(99999)}
                                                            onCalendarClose={() => setEstimatedFinishZIndex(1)}
                                                            disabled={!form.watch('period').from}
                                                            maxDate={form.watch('period').from ?? undefined}
                                                            labelStyle={styles.fieldsLabelStyle}
                                                        />
                                                    </View>
                                                </View>
                                                {!applicationsFinishLaterThanStart()
                                                    ? <MyAppText style={styles.errorMessage}>{translate('applicationsFinishMustBeAfterApplicationsStart')}</MyAppText>
                                                    : null
                                                }
                                            </View>
                                            : <View style={{ flexDirection: 'column', gap: 16 }}>
                                                <View style={[{ gap: 16, zIndex: 2 }, windowInfo.isMobile ? { flexDirection: 'column' } : { flexDirection: 'row' }]}>
                                                    <View style={[{ gap: 4, zIndex: 2 }]}>
                                                        <FormInput
                                                            value={startApplicationsDaysOffset.toString()}
                                                            onChangeText={(val) => setStartApplicationsDaysOffset(Number(val))}
                                                            required
                                                            label={translate('daysBeforeActivityToOpenApplications')}
                                                            mode='NUMBER'
                                                            labelStyle={styles.fieldsLabelStyle}
                                                            hintText={<View style={{ flexDirection: 'column', gap: 32, zIndex: 9999 }}>
                                                                <MyAppText style={{ color: theme.colors.white['500'] }}>{translate('daysBeforeActivityHintText1')}</MyAppText>
                                                                <MyAppText style={{ color: theme.colors.white['500'] }}>{translate('example')}: {translate('daysBeforeActivityHintText2')}</MyAppText>
                                                                <View>
                                                                    <MyAppText style={{ color: theme.colors.white['500'] }}>{translate('formula')}:</MyAppText>
                                                                    <MyAppText style={{ color: theme.colors.white['500'] }}>{translate('daysBeforeActivityHintText3')}:</MyAppText>
                                                                    <MyAppText style={{ color: theme.colors.white['500'] }}>{translate('example')}: {translate('daysBeforeActivityHintText4')}</MyAppText>
                                                                </View>
                                                            </View>}
                                                            viewStyle={[{ gap: 8 }]}
                                                        />
                                                        {(applicationQuantityDays != undefined && applicationQuantityDays > 0 && startApplicationsDaysOffset > 0 && startApplicationsDaysOffset < applicationQuantityDays)
                                                            ? <MyAppText style={styles.errorMessage}>{translate('mustBeBiggerOrEqualThan')}{' '}{translate('quantityOfDaysForApplications').toLowerCase()}</MyAppText>
                                                            : null
                                                        }
                                                    </View>

                                                    <FormInput
                                                        value={(applicationQuantityDays != undefined && applicationQuantityDays > 0) ? applicationQuantityDays.toString() : ''}
                                                        onChangeText={(val) => {
                                                            setApplicationQuantityDays(Number(val));
                                                            if (val === '' || Number(val) === 0) {
                                                                setClosingApplicationTime('');
                                                            }
                                                        }}
                                                        label={translate('quantityOfDaysForApplications')}
                                                        mode='NUMBER'
                                                        labelStyle={styles.fieldsLabelStyle}
                                                        viewStyle={[{ gap: 8 }]}
                                                        hintText={<View style={{ flexDirection: 'column', gap: 32, zIndex: 9999 }}>
                                                            <MyAppText style={{ color: theme.colors.white['500'] }}>{translate('quantityDaysForApplicationHintText1')}</MyAppText>
                                                            <MyAppText style={{ color: theme.colors.white['500'] }}>{translate('example')}: {translate('quantityDaysForApplicationHintText2')}</MyAppText>
                                                            <View>
                                                                <MyAppText style={{ color: theme.colors.white['500'] }}>{translate('formula')}:</MyAppText>
                                                                <MyAppText style={{ color: theme.colors.white['500'] }}>{translate('quantityDaysForApplicationHintText3')}:</MyAppText>
                                                                <MyAppText style={{ color: theme.colors.white['500'] }}>{translate('example')}: {translate('quantityDaysForApplicationHintText4')}</MyAppText>
                                                            </View>
                                                        </View>}
                                                        hintTextConfig={{
                                                            tooltipDirection: 'left'
                                                        }}
                                                    />
                                                </View>
                                                <View style={[{ gap: 16, zIndex: 1 }, windowInfo.isMobile ? { flexDirection: 'column' } : { flexDirection: 'row' }]}>
                                                    <View style={{ maxWidth: '25%', gap: 8 }}>
                                                        <View style={{ flexDirection: 'row', gap: 3 }}>
                                                            <MyAppText
                                                                style={styles.fieldsLabelStyle}>
                                                                {translate('openingTime')}
                                                            </MyAppText>
                                                            <MyAppText style={{ color: theme.colors.errors['300'] }}>(*)</MyAppText>
                                                        </View>
                                                        <View style={{ flexGrow: 1, maxWidth: 65 }}>
                                                            <TimePicker
                                                                disableClock={true}
                                                                format='HH:mm'
                                                                locale='pt-BR'
                                                                maxDetail='minute'
                                                                className={['light', 'lightAlt'].includes(UnistylesRuntime.themeName) ? ['inputColor', `inputColorLight`] : ['inputColor', `inputColorDark`]}
                                                                value={openingApplicationTime}
                                                                clearIcon={null}
                                                                onChange={(value) => {
                                                                    if (value) {
                                                                        setOpeningApplicationTime(value);
                                                                    }
                                                                }}
                                                            />
                                                        </View>
                                                    </View>
                                                    <View style={{ maxWidth: '25%', gap: 8 }}>
                                                        <View style={{ flexDirection: 'row', gap: 3 }}>
                                                            <MyAppText
                                                                style={styles.fieldsLabelStyle}>
                                                                {translate('closingTime')}
                                                            </MyAppText>
                                                            {(applicationQuantityDays != undefined && applicationQuantityDays > 0) && <MyAppText style={{ color: theme.colors.errors['300'] }}>(*)</MyAppText>}
                                                        </View>
                                                        <View style={{ flexGrow: 1, maxWidth: 65 }}>
                                                            <TimePicker
                                                                disableClock={true}
                                                                format='HH:mm'
                                                                locale='pt-BR'
                                                                maxDetail='minute'
                                                                className={['light', 'lightAlt'].includes(UnistylesRuntime.themeName) ? ['inputColor', `inputColorLight`] : ['inputColor', `inputColorDark`]}
                                                                value={closingApplicationTime}
                                                                clearIcon={null}
                                                                onChange={(value) => {
                                                                    if (value) {
                                                                        setClosingApplicationTime(value);
                                                                    }
                                                                }}
                                                            />
                                                        </View>
                                                    </View>
                                                </View>
                                            </View>
                                        }
                                    </SectionHeader>

                                    <SectionHeader
                                        title='activity'
                                        viewZIndex={2}
                                        endContent={
                                            <CustomTooltip
                                                content={<View style={{ flexDirection: 'column', gap: 32 }}>
                                                    <MyAppText style={{ color: theme.colors.white['500'] }}>{translate('templateVacancyGenerationActivityText1')}</MyAppText>
                                                    <MyAppText style={{ color: theme.colors.white['500'] }}>{translate('templateVacancyGenerationActivityText2')}</MyAppText>
                                                </View>
                                                }
                                            />
                                        }
                                    >
                                        {vacancyConfiguration.map((config, configIndex) => (
                                            <>
                                                <View key={configIndex} style={[{ gap: 16 }, windowInfo.isMobile ? { flexDirection: 'column' } : { flexDirection: 'row' }]}>
                                                    <View style={{ flexDirection: 'column', flex: 1, gap: 16 }}>
                                                        <View style={{ flex: 1, gap: 8 }}>
                                                            <View style={{ flexDirection: 'row', gap: 3 }}>
                                                                <MyAppText
                                                                    style={styles.fieldsLabelStyle}>
                                                                    {translate('activityStartTime')}
                                                                </MyAppText>
                                                                <MyAppText style={{ color: theme.colors.errors['300'] }}>(*)</MyAppText>
                                                            </View>
                                                            <View style={{ maxWidth: 65 }}>
                                                                <TimePicker
                                                                    disableClock={true}
                                                                    format='HH:mm'
                                                                    locale='pt-BR'
                                                                    maxDetail='minute'
                                                                    className={['light', 'lightAlt'].includes(UnistylesRuntime.themeName) ? ['inputColor', `inputColorLight`] : ['inputColor', `inputColorDark`]}
                                                                    value={config.activityTime}
                                                                    clearIcon={null}
                                                                    onChange={(value) => {
                                                                        if (value) {
                                                                            const copy = vacancyConfiguration.map((auxVal, auxIndex) => {
                                                                                if (auxIndex === configIndex) {
                                                                                    auxVal.activityTime = value;
                                                                                }

                                                                                return auxVal;
                                                                            });

                                                                            setVacancyConfiguration(copy);
                                                                        }
                                                                    }}
                                                                />
                                                            </View>
                                                        </View>
                                                        <FormInput
                                                            value={config.vacancyDuration.toString()}
                                                            onChangeText={(val) => {
                                                                const copy = vacancyConfiguration.map((auxVal, auxIndex) => {
                                                                    if (auxIndex === configIndex) {
                                                                        auxVal.vacancyDuration = Number(val);
                                                                    }

                                                                    return auxVal;
                                                                });

                                                                setVacancyConfiguration(copy);
                                                            }}
                                                            required
                                                            label={translate('activityDurationInHours')}
                                                            mode='NUMBER'
                                                            labelStyle={styles.fieldsLabelStyle}
                                                            viewStyle={[{ gap: 8 }, windowInfo.isMobile ? {} : { maxWidth: '75%' }]}
                                                        />
                                                    </View>
                                                    <View style={{ flex: 1, flexDirection: 'row' }}>
                                                        <View style={[{ gap: 8, flex: 1 }, windowInfo.isMobile ? { flexDirection: 'column' } : { flexDirection: 'row', flexWrap: 'wrap' }]}>
                                                            {Object.entries(config.days).map((day, index) => {
                                                                return <MyCheckbox
                                                                    key={`${day[0]}_${index}`}
                                                                    style={{ flexBasis: '32%' }}
                                                                    checked={day[1]}
                                                                    label={translate(day[0])}
                                                                    setChecked={val => {
                                                                        const copy = vacancyConfiguration.map((auxVal, auxIndex) => {
                                                                            if (auxIndex === configIndex) {
                                                                                auxVal.days = {
                                                                                    ...auxVal.days,
                                                                                    [day[0]]: val,
                                                                                };
                                                                            }

                                                                            return auxVal;
                                                                        });
                                                                        setVacancyConfiguration(copy);
                                                                    }}
                                                                    customIcon={<FontAwesomeIcon icon={faCheck} fontSize={10} color={theme.colors.white['500']} />}
                                                                    checkBoxStyle={{
                                                                        backgroundColor: day[1]
                                                                            ? theme.colors.primary['500']
                                                                            : 'transparent',
                                                                        borderWidth: 1,
                                                                        borderColor: theme.colors.neutral['300']
                                                                    }}
                                                                />;
                                                            })}
                                                        </View>
                                                        <View style={{ flexDirection: 'column', justifyContent: 'center' }}>
                                                            {(vacancyConfiguration.length > 1) &&
                                                                <CustomButton
                                                                    onPress={() => removeVacancyConfiguration(configIndex)}
                                                                    icon={faX}
                                                                    style={{ backgroundColor: 'transparent' }}
                                                                    textStyle={{ color: theme.colors.errors['300'] }}
                                                                    contentContainerStyle={{ justifyContent: 'center' }}
                                                                    type='destructive'
                                                                />
                                                            }
                                                            {(configIndex === Object.entries(vacancyConfiguration).length - 1) &&
                                                                <CustomButton
                                                                    onPress={addVacancyConfiguration}
                                                                    icon={faSquarePlus}
                                                                    style={{ backgroundColor: 'transparent' }}
                                                                    textStyle={{ color: theme.colors.primary['500'], fontSize: 24 }}
                                                                    contentContainerStyle={{ justifyContent: 'center' }}
                                                                    type='primary'
                                                                />
                                                            }
                                                        </View>
                                                    </View>
                                                </View>
                                                {!(configIndex === Object.entries(vacancyConfiguration).length - 1) && <Divider style={{ zIndex: -1, marginVertical: 8 }} color={theme.colors.primary['200']} />}
                                            </>
                                        ))}
                                    </SectionHeader>
                                </ScrollView>;
                            }
                        }}
                    </Tabs>

                    <FormActions
                        buttonStyle={{ paddingHorizontal: 16, paddingVertical: 9, }}
                        cancelText='goBack'
                        confirmText='generateVacancies'
                        onSubmit={formSubmit}
                        onClose={closeModal}
                        disabled={isLoading || !isFormValid()}
                        isLoading={isLoading}
                    />
                </View>
            </View>
        </Modal>
    );
}

const styleSheet = createStyleSheet((theme) => ({
    sectionHeaderTitle: {
        color: theme.colors.primary['500'],
        fontWeight: '600',
        fontSize: 16,
    },
    container: {
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        backgroundColor: '#31313199',
        flex: 1,
    },
    formContainer: (isMobile: boolean) => ({
        width: isMobile ? '95%' : '60%',
        backgroundColor: theme.colors.container.background,
        borderRadius: 4,
        rowGap: 32,
        padding: 32,
        maxHeight: isMobile ? '95%' : 690,
    }),
    header: {
        fontSize: 18,
        fontWeight: '700',
        flexWrap: 'wrap',
        color: theme.colors.modal.header,
    },
    subheader: {
        fontSize: 16,
        fontWeight: '400',
        flexWrap: 'wrap',
        color: theme.colors.modal.subheader,
    },
    reasonLabelStyle: {
        color: theme.colors.neutral['500'],
        fontWeight: '600',
        fontSize: 14,
    },
    toggleLabelStyle: {
        fontWeight: '600',
        fontSize: 14,
    },
    fieldsLabelStyle: {
        color: theme.colors.neutral['500'],
        fontWeight: '600',
        fontSize: 14,
    },
    errorMessage: {
        color: theme.colors.errors['300'],
        fontWeight: '500',
        fontSize: 12,
    },
    templateInfo: {
        flex: 1,
        flexDirection: 'column',
    },
    templateInfoItem: {
        flexDirection: 'column',
        gap: 4,
    },
    templateInfoItemTitle: {
        fontSize: 16,
        fontWeight: '700',
        color: theme.colors.primary['500']
    },
    templateInfoItemSubTitle: {
        fontSize: 14,
        fontWeight: '700',
        color: theme.colors.neutral['500']
    },
    templateInfoItemDescription: {
        fontSize: 14,
        fontWeight: '500',
        color: theme.colors.neutral['400']
    },
}));
